import { useState, useCallback } from 'react';
import axios from 'axios';
import { formatApiErrorDetail } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to get stored token
const getStoredToken = () => {
  return localStorage.getItem('access_token');
};

// Configure axios defaults
axios.defaults.withCredentials = true;

// Add interceptor to include token in all requests
axios.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const useApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally stable: API base URL and axios config don't change at runtime
  const request = useCallback(async (method, endpoint, data = null) => {
    setLoading(true);
    setError(null);
    try {
      const token = getStoredToken();
      const config = { 
        method, 
        url: `${API}${endpoint}`,
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      };
      if (data) config.data = data;
      const response = await axios(config);
      return { success: true, data: response.data };
    } catch (err) {
      const errorMessage = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  const get = useCallback((endpoint) => request('GET', endpoint), [request]);
  const post = useCallback((endpoint, data) => request('POST', endpoint, data), [request]);
  const put = useCallback((endpoint, data) => request('PUT', endpoint, data), [request]);
  const del = useCallback((endpoint) => request('DELETE', endpoint), [request]);

  return { get, post, put, del, loading, error, setError };
};

// Static API functions for use outside hooks
export const api = {
  get: async (endpoint) => {
    const token = getStoredToken();
    const response = await axios.get(`${API}${endpoint}`, { 
      withCredentials: true,
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    return response.data;
  },
  post: async (endpoint, data) => {
    const token = getStoredToken();
    const response = await axios.post(`${API}${endpoint}`, data, { 
      withCredentials: true,
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    return response.data;
  },
  put: async (endpoint, data) => {
    const token = getStoredToken();
    const response = await axios.put(`${API}${endpoint}`, data, { 
      withCredentials: true,
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    return response.data;
  },
  delete: async (endpoint) => {
    const token = getStoredToken();
    const response = await axios.delete(`${API}${endpoint}`, { 
      withCredentials: true,
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    return response.data;
  },
  upload: async (endpoint, file) => {
    const token = getStoredToken();
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API}${endpoint}`, formData, {
      withCredentials: true,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }
};
